// Geo-intelligence client layer.
//
// Reads classified geopolitical events from the Supabase `geo_events` table
// (written only by the geoclassify Netlify Function — same one-shot cache
// pattern as news_summaries) and computes the portfolio overlap for each
// event. The overlap is 100% deterministic, client-side — the AI only
// classifies the article; it never sees the user's portfolio.
import { supabase } from "../lib/supabase";
import { eventNames } from "./newsMatch";
import { isGeopolitical } from "../constants/geopolitical";
import { PILL } from "../constants/tokens";
import { selectCandidates, geoSectorsForAppSector } from "./geoCandidates";
import { valuedHoldings } from "./portfolioExposures";

// The classification function is told to use exactly these sector names, so
// client matching is a plain string comparison (no fuzzy logic).
export const GEO_SECTOR_TAXONOMY = [
  "שבבים", "אנרגיה", "רכב", "תעשייה", "קמעונות/מזון", "ייצור טכנולוגי",
  "רשתות חברתיות", "מחשוב קוואנטי", "סייבר", "ביטחון/חלל", "פארמה",
  "פיננסים", "שילוח וסחר",
];

// ── Fetch (last 48h, newest first) ──────────────────────────────────────────
const hasHebrewHeadline = ev => typeof ev?.headline_he === "string" && /[\u0590-\u05FF]/.test(ev.headline_he);

// `{ all: true }`: every row, also those still without their Hebrew headline ·
// for knowing which articles are already classified (GeoIntelTab), never for
// showing them.
export async function fetchGeoEvents({ all = false } = {}) {
  if (!supabase) return [];
  const since = new Date(Date.now() - 48 * 60 * 60 * 1000).toISOString();
  const run = (cols) => supabase
    .from("geo_events")
    .select(cols)
    .gte("created_at", since)
    .order("published_at", { ascending: false })
    .limit(60);

  const WITH_HE = "id, source_url, headline, headline_he, summary_he, category, regions, sectors, tickers, published_at, created_at, source_name, source_type";
  const LEGACY  = "id, source_url, headline, summary_he, category, regions, sectors, tickers, published_at, created_at, source_name";

  let { data, error } = await run(WITH_HE);
  // headline_he / source_type may not exist yet (migration pending) — retry
  // without them. Rows then carry no source_type and isOfficial() says false.
  if (error && (error.code === "42703" || /headline_he|source_type/.test(error.message || ""))) {
    ({ data, error } = await run(LEGACY));
  }
  if (error) {
    // Table may not exist yet (migration pending) — degrade silently.
    console.warn("[geoEvents] fetch failed:", error.code, error.message);
    return [];
  }
  // An event without its Hebrew headline is not shown (26/10/2026): the app
  // shows no English headline, and the hourly run translates what is missing
  // (geo-classify-scheduled.js). It is not counted either, so no screen
  // reports an event it cannot show.
  // Rows that fail the geopolitical rule (constants/geopolitical.js) are read
  // as "none": not shown as geopolitical, not counted, not tied to a portfolio.
  return (data || [])
    .filter(ev => all || hasHebrewHeadline(ev))
    .map(ev => (isGeopolitical(ev) ? ev : { ...ev, category: "none" }));
}

// Timestamp (ms) of the most recent classification run — max(created_at) over
// all geo_events rows. Every scanned article (incl. category 'none') becomes a
// row, so this reflects when the scanner last ran, not the last relevant event.
// Returns null when the table is empty / unavailable (caller hides the line).
export async function fetchLastScan() {
  if (!supabase) return null;
  const { data, error } = await supabase
    .from("geo_events")
    .select("created_at")
    .order("created_at", { ascending: false })
    .limit(1);
  if (error || !data || !data.length) return null;
  const t = Date.parse(data[0].created_at);
  return Number.isFinite(t) ? t : null;
}

// ─── What the scan did, over a window (sprint 13, 2.2) ──────────────────────
// Three counts, straight from geo_events, each with its own meaning:
//   scanned  = every article the scan classified in the window (incl. 'none'),
//              an exact count from the database, not the length of a page
//   events   = those that pass isGeopolitical (constants/geopolitical.js)
//   relevant = events whose headline names a held or watched company ("hot");
//              no sector-level match since 2026-09-26
// The window is rolling (the last `hours`), not "since midnight": a count
// from local midnight read "היום · 1 כתבות" at 00:30 (bug, 28.09). A select
// used to be capped at 200/500 rows and reported the cap as the count.
// `capped` is true when the events page itself hit its limit; the caller then
// says "לפחות". Returns null when there is no data (the caller hides it).
const EVENTS_PAGE = 1000;
export async function fetchScanStats(portfolio, watchlist, { hours }) {
  if (!supabase) return null;
  const since = new Date(Date.now() - hours * 3600 * 1000).toISOString();
  const [all, rows] = await Promise.all([
    supabase.from("geo_events").select("id", { count: "exact", head: true }).gte("created_at", since),
    supabase.from("geo_events")
      .select("category, tickers, sectors, headline, headline_he, summary_he, source_type, source_name, source_url")
      .gte("created_at", since)
      .neq("category", "none")
      .limit(EVENTS_PAGE),
  ]);
  if (all.error || rows.error || !all.count) return null;
  // Counted as shown: an event without its Hebrew headline is not on any
  // screen yet (fetchGeoEvents, 26/10/2026), so it is not counted as one.
  const events = (rows.data || []).filter(ev => hasHebrewHeadline(ev) && isGeopolitical(ev));
  const relevant = events.filter(ev => computeOverlap(ev, portfolio, watchlist).level === "hot").length;
  return { scanned: all.count, events: events.length, relevant, capped: (rows.data || []).length >= EVENTS_PAGE, hours };
}

// ── Deterministic overlap ────────────────────────────────────────────────────
// Returns { level, matchedSymbols, heldSymbols, watchedSymbols, weightPct,
//           matchedSector, sectorSymbols, otherTickers }
//   hot — a ticker in the event is held or watched (direct overlap)
//   geo — a sector in the event exists in the portfolio (indirect)
//   ok  — no overlap
//
// `hot` splits matchedSymbols into held vs watched, because "בתיק שלך" is false
// for a watchlist-only match and the caller has to be able to word it honestly.
// `geo` carries sectorSymbols (which of the user's symbols put that sector in
// the portfolio) and otherTickers (the companies the article is actually about,
// none of which the user owns) — an indirect match must be readable as
// indirect, not as a hit on the company in the headline.
//
// A direct match (`hot`, "בתיק שלך") needs the event's HEADLINE to name the
// company · the ticker or its name, in either language (utils/newsMatch.js,
// eventNames). The classifier's `tickers` list alone does not count: it is a
// guess about what the story touches, and on 25.09 it tied a crypto story to
// AAPL. Same rule as the news screen.
export function computeOverlap(event, portfolio = [], watchlist = []) {

  const heldSet    = new Set(portfolio.map(p => p.symbol));
  const nameOf = s => (s.name && s.name !== s.symbol ? s.name : "");

  const heldSymbols    = portfolio.filter(p => eventNames(event, p.symbol, nameOf(p))).map(p => p.symbol);
  const watchedSymbols = watchlist.filter(w => !heldSet.has(w.symbol) && eventNames(event, w.symbol, nameOf(w))).map(w => w.symbol);
  const matchedSymbols = [...heldSymbols, ...watchedSymbols];
  if (matchedSymbols.length > 0) {
    // Weight: share of the portfolio held in the matched symbols, from the
    // exposures screen's own function, and 0 (not shown) until every price
    // has arrived · a pending holding carries its buy price (sprint 14, 3.1).
    // Unrounded: the tie prints one decimal, like every other screen.
    const pending = portfolio.some(s => s.quote === "pending");
    const weightPct = pending ? 0 : valuedHoldings(portfolio).rows
      .filter(r => matchedSymbols.includes(r.symbol))
      .reduce((a, r) => a + r.weightPct, 0);
    return {
      level: "hot", matchedSymbols, heldSymbols, watchedSymbols, weightPct,
      matchedSector: null, sectorSymbols: [], otherTickers: [],
    };
  }

  // No sector tie (2026-09-26, "מידע, לא ייעוץ"). An event about a company
  // the user does not hold · NVDA, MSFT · is not tied to their portfolio at
  // all, not even as "בסקטור שלך": a personal tie to something the user does
  // not own is exactly the "personal fit" the regulator's test looks for.
  // Only a ticker actually held (or watched) and named in the headline counts.
  return {
    level: "ok", matchedSymbols: [], heldSymbols: [], watchedSymbols: [], weightPct: 0,
    matchedSector: null, sectorSymbols: [], otherTickers: [],
  };
}

// ── Overlap badge ────────────────────────────────────────────────────────────
// One wording and one tone for the overlap statement, wherever it appears —
// the calendar and the geo-intel list were saying different things about the
// same three states, and one of them ("במעקב" for a sector match) named the
// wrong mechanism entirely.
//
// `hot` splits: computeOverlap keeps held and watched apart precisely so this
// can stay true. A watchlist-only match is not "in your portfolio".
//
// `ok` uses PILL.ok, not PILL.neutral — "no overlap" is a stated finding about
// the user's portfolio, where neutral is quiet furniture (a ticker chip, an
// empty-state line).
export function overlapTag(o) {
  if (!o) return null;
  if (o.level === "hot") {
    return o.heldSymbols.length > 0
      ? { label: "בתיק שלך", style: PILL.hot }
      : { label: "במעקב שלך", style: PILL.geo };
  }
  if (o.level === "geo") return { label: "בסקטור שלך", style: PILL.geo };
  return { label: "ללא חפיפה", style: PILL.ok };
}

// ── Official documents ───────────────────────────────────────────────────────
// A geo_events row is either a Finnhub news article ('news') or a Federal
// Register document ('official' — written by federal-register-scheduled.js).
// A document is a primary source: it IS the event, where an article reports
// it. That difference is shown on every card and decides the order below.
export function isOfficial(ev) {
  return ev?.source_type === "official";
}

// One label and one style wherever an official document is marked. PILL.ok is
// the neutral-strong step: a document is a stated fact about provenance, not
// an overlap (hot/geo) and not furniture (neutral). No reserved hue.
export const OFFICIAL_TAG = { label: "מסמך רשמי", icon: "bank", style: PILL.ok };

// ── Classification trigger ───────────────────────────────────────────────────
// Sends articles the table doesn't know yet to the geoclassify function —
// but only the ones that overlap THIS user's holdings, by the same
// deterministic rule the hourly cron applies (utils/geoCandidates.js): a held
// or watched ticker named in the article, or a keyword of a sector one of the
// holdings resolves to. The general feed used to go to Gemini whole; with a
// hundred users that is thousands of calls a day on articles no one holds
// anything in. The sector comes from each holding's live `sector` (Finnhub
// profile2 via sectorClassify), never from a table — a holding whose lookup
// is still in flight contributes no sector, and matches by ticker only.
// Hard daily cap of 30 classifications (localStorage counter) protects the
// Gemini quota; the function's own source_url cache makes retries free.
const DAILY_CAP = 30;
const CAP_KEY = "si_geo_classify_count";

function todayKey() {
  return new Date().toISOString().slice(0, 10);
}

function readDailyCount() {
  try {
    const raw = JSON.parse(localStorage.getItem(CAP_KEY) || "{}");
    return raw.day === todayKey() ? (raw.count || 0) : 0;
  } catch { return 0; }
}

function bumpDailyCount(n) {
  try {
    localStorage.setItem(CAP_KEY, JSON.stringify({ day: todayKey(), count: readDailyCount() + n }));
  } catch { /* private mode — cap falls back to per-session behavior */ }
}

let _classifyInFlight = false;

// The relevance sets for the current user: held + watched symbols, and the
// geo-taxonomy sectors their live sectors open. Exported for the test.
export function relevanceSets(portfolio = [], watchlist = []) {
  const tickers = new Set(
    [...portfolio, ...watchlist].map(s => String(s.symbol || "").toUpperCase()).filter(Boolean)
  );
  const sectors = new Set();
  for (const s of [...portfolio, ...watchlist]) {
    for (const sec of geoSectorsForAppSector(s.sector)) sectors.add(sec);
  }
  return { tickers, sectors };
}

// articles: mapped Finnhub articles ({ title, excerpt, source, url, datetime, symbols }).
// knownUrls: Set of source_urls already present in geo_events.
// portfolio / watchlist: the user's rows, for the relevance filter.
// Returns the number of articles actually sent.
export async function classifyNewArticles(articles, knownUrls, portfolio = [], watchlist = []) {
  if (_classifyInFlight) return 0;
  const budget = DAILY_CAP - readDailyCount();
  if (budget <= 0) return 0;

  const { tickers, sectors } = relevanceSets(portfolio, watchlist);
  // Nothing held and nothing watched → nothing is relevant, and nothing is sent.
  if (tickers.size === 0 && sectors.size === 0) return 0;

  const fresh = selectCandidates(
    articles.filter(a => a.url && a.title),
    tickers, sectors, knownUrls, budget
  );
  if (fresh.length === 0) return 0;

  _classifyInFlight = true;
  let sent = 0;
  try {
    for (const a of fresh) {
      try {
        // Sequential with a small gap — keeps function + Gemini load gentle.
        // eslint-disable-next-line no-await-in-loop
        await fetch("/.netlify/functions/geoclassify", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            title:       a.title,
            excerpt:     a.excerpt || "",
            lang:        "he",
            sourceUrl:   a.url,
            sourceName:  a.source || "",
            publishedAt: a.datetime ? a.datetime * 1000 : null,
          }),
        });
        sent += 1;
        // eslint-disable-next-line no-await-in-loop
        await new Promise(r => setTimeout(r, 400));
      } catch {
        // Single-article failure is not fatal — skip silently, cache covers retries.
      }
    }
  } finally {
    if (sent > 0) bumpDailyCount(sent);
    _classifyInFlight = false;
  }
  return sent;
}

// Sort: hot first, then geo, then the rest. Within a level an official
// document precedes a news article — the document is the event, the article
// is a report of it. Ties by published_at desc. Overlap itself is untouched:
// this orders computeOverlap's answers, it does not change them.
export function sortByOverlap(events, portfolio, watchlist) {
  const rank = { hot: 0, geo: 1, ok: 2 };
  return [...events]
    .map(ev => ({ ev, overlap: computeOverlap(ev, portfolio, watchlist) }))
    .sort((a, b) => {
      const r = rank[a.overlap.level] - rank[b.overlap.level];
      if (r !== 0) return r;
      const o = Number(isOfficial(b.ev)) - Number(isOfficial(a.ev));
      if (o !== 0) return o;
      return new Date(b.ev.published_at || 0) - new Date(a.ev.published_at || 0);
    });
}
